import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  CheckCircleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getSession, setSession } from '../../auth/session';

const PERFIL_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'visualizacao', label: 'Visualizacao' },
  { value: 'leitura', label: 'Leitura' },
  { value: 'colaborador', label: 'Colaborador' },
];

const ROLE_GLOBAL_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'usuario', label: 'Usuario' },
];

const PERMISSAO_PRESETS = [
  {
    key: 'gestao',
    label: 'Gestao',
    description: 'Administra a empresa e usa todos os modulos liberados.',
    perfil: 'admin',
    module_scope_mode: 'company',
    module_keys: [],
  },
  {
    key: 'operacao',
    label: 'Operacao',
    description: 'Opera rotinas financeiras nos modulos da empresa.',
    perfil: 'financeiro',
    module_scope_mode: 'company',
    module_keys: [],
  },
  {
    key: 'leitura',
    label: 'Leitura',
    description: 'Consulta dados sem alterar cadastros e lancamentos.',
    perfil: 'leitura',
    module_scope_mode: 'company',
    module_keys: [],
  },
];

function createEmptyEmpresaForm() {
  return {
    id: null,
    nome: '',
    cnpj: '',
    telefone: '',
    email: '',
    endereco: '',
    ativa: true,
    modules: {},
    criar_plano_contas_padrao: true,
  };
}

function createEmptyUsuarioForm() {
  return {
    id: null,
    nome: '',
    email: '',
    senha: '',
    role_global: 'usuario',
    ativo: true,
  };
}

function createEmptyPermissaoForm() {
  return {
    id: null,
    usuario_id: '',
    empresa_id: '',
    perfil: 'colaborador',
    ativo: true,
    module_scope_mode: 'company',
    module_keys: [],
  };
}

function getCompanyModuleMap(empresa = {}) {
  if (empresa?.modules_map && typeof empresa.modules_map === 'object') {
    return Object.keys(empresa.modules_map).reduce((acc, key) => {
      acc[key] = empresa.modules_map[key]?.enabled === true;
      return acc;
    }, {});
  }

  if (Array.isArray(empresa?.modules)) {
    return empresa.modules.reduce((acc, moduleItem) => {
      const key = String(moduleItem?.key || '').trim().toLowerCase();
      if (key) acc[key] = moduleItem?.enabled === true;
      return acc;
    }, {});
  }

  return {
    financeiro: empresa?.modulo_financeiro_ativo === true,
    construtor: empresa?.modulo_construtor_ativo === true,
    radar_lucro: empresa?.modulo_radar_lucro_ativo === true,
    solar: empresa?.modulo_solar_ativo === true,
  };
}

function buildModuleSelection(moduleCatalog = [], source = {}) {
  const sourceMap = getCompanyModuleMap(source);
  return moduleCatalog.reduce((acc, moduleItem) => {
    acc[moduleItem.key] = sourceMap[moduleItem.key] === true;
    return acc;
  }, {});
}

function syncEmpresaInSession(empresaSalva) {
  if (!empresaSalva?.id) return;

  const session = getSession();
  if (!session?.empresas?.length) return;

  const empresasAtualizadas = session.empresas.map((item) =>
    item.empresa_id === empresaSalva.id
      ? {
          ...item,
          nome: empresaSalva.nome,
          modulo_financeiro_ativo: empresaSalva.modulo_financeiro_ativo,
          modulo_construtor_ativo: empresaSalva.modulo_construtor_ativo,
          modulo_radar_lucro_ativo: empresaSalva.modulo_radar_lucro_ativo,
          modulo_solar_ativo: empresaSalva.modulo_solar_ativo,
          modules: empresaSalva.modules || [],
          modules_map: empresaSalva.modules_map || {},
          module_keys: empresaSalva.module_keys || [],
          capability_keys: empresaSalva.capability_keys || [],
          capabilities_map: empresaSalva.capabilities_map || {},
          access_source: empresaSalva.access_source || item.access_source,
        }
      : item
  );

  setSession({
    ...session,
    empresas: empresasAtualizadas,
  });
}

function renderModuleBadges(modules = [], emptyLabel = 'Nenhum modulo') {
  if (!modules.length) {
    return <span className="text-xs text-gray-500">{emptyLabel}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {modules.map((moduleItem) => (
        <span key={moduleItem.key} className="badge-success">
          {moduleItem.nome}
        </span>
      ))}
    </div>
  );
}

const GestaoSaaS = () => {
  const [overview, setOverview] = useState({ empresas: 0, usuarios: 0, permissoes: 0 });
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [permissoes, setPermissoes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showEmpresaModal, setShowEmpresaModal] = useState(false);
  const [showUsuarioModal, setShowUsuarioModal] = useState(false);
  const [showPermissaoModal, setShowPermissaoModal] = useState(false);

  const [empresaForm, setEmpresaForm] = useState(createEmptyEmpresaForm());
  const [usuarioForm, setUsuarioForm] = useState(createEmptyUsuarioForm());
  const [permissaoForm, setPermissaoForm] = useState(createEmptyPermissaoForm());
  const [permissaoSearch, setPermissaoSearch] = useState('');
  const [permissaoEmpresaFiltro, setPermissaoEmpresaFiltro] = useState('todas');
  const [permissaoStatusFiltro, setPermissaoStatusFiltro] = useState('ativas');
  const [usuarioModalSearch, setUsuarioModalSearch] = useState('');
  const [empresaModalSearch, setEmpresaModalSearch] = useState('');

  const empresasAtivas = useMemo(() => empresas.filter((item) => item.ativa).length, [empresas]);
  const usuariosAtivos = useMemo(() => usuarios.filter((item) => item.ativo).length, [usuarios]);

  const moduleCatalogMap = useMemo(
    () =>
      moduleCatalog.reduce((acc, moduleItem) => {
        acc[moduleItem.key] = moduleItem;
        return acc;
      }, {}),
    [moduleCatalog]
  );

  const empresaSelecionadaNaPermissao = useMemo(
    () => empresas.find((empresa) => empresa.id === permissaoForm.empresa_id) || null,
    [empresas, permissaoForm.empresa_id]
  );

  const modulosDisponiveisParaPermissao = useMemo(() => {
    const companyModuleMap = getCompanyModuleMap(empresaSelecionadaNaPermissao);
    return moduleCatalog.map((moduleItem) => ({
      ...moduleItem,
      company_enabled: companyModuleMap[moduleItem.key] === true,
    }));
  }, [empresaSelecionadaNaPermissao, moduleCatalog]);

  const usuariosFiltradosModal = useMemo(() => {
    const term = usuarioModalSearch.trim().toLowerCase();
    if (!term) return usuarios;
    return usuarios.filter((usuario) =>
      [usuario.nome, usuario.email].some((value) => String(value || '').toLowerCase().includes(term))
    );
  }, [usuarioModalSearch, usuarios]);

  const empresasFiltradasModal = useMemo(() => {
    const term = empresaModalSearch.trim().toLowerCase();
    if (!term) return empresas;
    return empresas.filter((empresa) =>
      [empresa.nome, empresa.cnpj, empresa.email].some((value) => String(value || '').toLowerCase().includes(term))
    );
  }, [empresaModalSearch, empresas]);

  const permissoesFiltradas = useMemo(() => {
    const term = permissaoSearch.trim().toLowerCase();
    return permissoes.filter((item) => {
      const matchesSearch =
        !term ||
        [item.usuario_nome, item.usuario_email, item.empresa_nome, item.perfil]
          .some((value) => String(value || '').toLowerCase().includes(term));
      const matchesEmpresa = permissaoEmpresaFiltro === 'todas' || item.empresa_id === permissaoEmpresaFiltro;
      const matchesStatus =
        permissaoStatusFiltro === 'todas' ||
        (permissaoStatusFiltro === 'ativas' && item.ativo) ||
        (permissaoStatusFiltro === 'inativas' && !item.ativo);

      return matchesSearch && matchesEmpresa && matchesStatus;
    });
  }, [permissaoEmpresaFiltro, permissaoSearch, permissaoStatusFiltro, permissoes]);

  const activePresetKey = useMemo(() => {
    const exactPreset = PERMISSAO_PRESETS.find(
      (preset) =>
        preset.perfil === permissaoForm.perfil &&
        preset.module_scope_mode === permissaoForm.module_scope_mode &&
        preset.module_keys.length === permissaoForm.module_keys.length
    );
    return exactPreset?.key || 'custom';
  }, [permissaoForm.module_keys.length, permissaoForm.module_scope_mode, permissaoForm.perfil]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [overviewRs, moduleCatalogRs, empresasRs, usuariosRs, permissoesRs] = await Promise.all([
        axios.get('/api/admin/overview'),
        axios.get('/api/admin/module-catalog'),
        axios.get('/api/admin/empresas'),
        axios.get('/api/admin/usuarios'),
        axios.get('/api/admin/permissoes'),
      ]);

      setOverview(overviewRs.data || { empresas: 0, usuarios: 0, permissoes: 0 });
      setModuleCatalog(Array.isArray(moduleCatalogRs.data) ? moduleCatalogRs.data : []);
      setEmpresas(Array.isArray(empresasRs.data) ? empresasRs.data : []);
      setUsuarios(Array.isArray(usuariosRs.data) ? usuariosRs.data : []);
      setPermissoes(Array.isArray(permissoesRs.data) ? permissoesRs.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar dados administrativos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const abrirNovaEmpresa = () => {
    setEmpresaForm({
      ...createEmptyEmpresaForm(),
      modules: buildModuleSelection(moduleCatalog),
    });
    setShowEmpresaModal(true);
  };

  const abrirEditarEmpresa = (empresa) => {
    setEmpresaForm({
      id: empresa.id,
      nome: empresa.nome || '',
      cnpj: empresa.cnpj || '',
      telefone: empresa.telefone || '',
      email: empresa.email || '',
      endereco: empresa.endereco || '',
      ativa: empresa.ativa !== false,
      modules: buildModuleSelection(moduleCatalog, empresa),
      criar_plano_contas_padrao: false,
    });
    setShowEmpresaModal(true);
  };

  const salvarEmpresa = async () => {
    try {
      if (!empresaForm.nome.trim()) {
        toast.error('Nome da empresa e obrigatorio');
        return;
      }

      const payload = {
        nome: empresaForm.nome.trim(),
        cnpj: empresaForm.cnpj || null,
        telefone: empresaForm.telefone || null,
        email: empresaForm.email || null,
        endereco: empresaForm.endereco || null,
        ativa: empresaForm.ativa,
        modules: { ...empresaForm.modules },
      };
      if (!empresaForm.id) {
        payload.criar_plano_contas_padrao = empresaForm.criar_plano_contas_padrao !== false;
      }

      let empresaSalva = null;
      if (empresaForm.id) {
        const rs = await axios.put(`/api/admin/empresas/${empresaForm.id}`, payload);
        empresaSalva = rs.data?.empresa || null;
        toast.success('Empresa atualizada');
      } else {
        const rs = await axios.post('/api/admin/empresas', payload);
        empresaSalva = rs.data?.empresa || null;
        toast.success('Empresa criada');
      }

      syncEmpresaInSession(empresaSalva);
      setShowEmpresaModal(false);
      await carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar empresa');
    }
  };

  const abrirNovoUsuario = () => {
    setUsuarioForm(createEmptyUsuarioForm());
    setShowUsuarioModal(true);
  };

  const abrirEditarUsuario = (usuario) => {
    setUsuarioForm({
      id: usuario.id,
      nome: usuario.nome || '',
      email: usuario.email || '',
      senha: '',
      role_global: usuario.role_global || 'usuario',
      ativo: usuario.ativo !== false,
    });
    setShowUsuarioModal(true);
  };

  const salvarUsuario = async () => {
    try {
      if (!usuarioForm.nome.trim()) {
        toast.error('Nome do usuario e obrigatorio');
        return;
      }
      if (!usuarioForm.email.trim()) {
        toast.error('Email e obrigatorio');
        return;
      }
      if (!usuarioForm.id && !usuarioForm.senha) {
        toast.error('Senha e obrigatoria para novo usuario');
        return;
      }

      const payload = {
        nome: usuarioForm.nome.trim(),
        email: usuarioForm.email.trim(),
        role_global: usuarioForm.role_global,
        ativo: usuarioForm.ativo,
      };

      if (usuarioForm.senha?.trim()) {
        payload.senha = usuarioForm.senha.trim();
      }

      if (usuarioForm.id) {
        await axios.put(`/api/admin/usuarios/${usuarioForm.id}`, payload);
        toast.success('Usuario atualizado');
      } else {
        await axios.post('/api/admin/usuarios', payload);
        toast.success('Usuario criado');
      }

      setShowUsuarioModal(false);
      await carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar usuario');
    }
  };

  const abrirNovaPermissao = () => {
    setPermissaoForm(createEmptyPermissaoForm());
    setUsuarioModalSearch('');
    setEmpresaModalSearch('');
    setShowPermissaoModal(true);
  };

  const abrirEditarPermissao = (permissao) => {
    setPermissaoForm({
      id: permissao.id,
      usuario_id: permissao.usuario_id,
      empresa_id: permissao.empresa_id,
      perfil: permissao.perfil || 'colaborador',
      ativo: permissao.ativo !== false,
      module_scope_mode: permissao.explicit_module_scope ? 'custom' : 'company',
      module_keys: Array.isArray(permissao.module_keys) ? permissao.module_keys : [],
    });
    setUsuarioModalSearch('');
    setEmpresaModalSearch('');
    setShowPermissaoModal(true);
  };

  const aplicarPresetPermissao = (preset) => {
    setPermissaoForm({
      ...permissaoForm,
      perfil: preset.perfil,
      module_scope_mode: preset.module_scope_mode,
      module_keys: [...preset.module_keys],
    });
  };

  const salvarPermissao = async () => {
    try {
      if (!permissaoForm.usuario_id || !permissaoForm.empresa_id) {
        toast.error('Usuario e empresa sao obrigatorios');
        return;
      }

      const payload = {
        usuario_id: permissaoForm.usuario_id,
        empresa_id: permissaoForm.empresa_id,
        perfil: permissaoForm.perfil,
        ativo: permissaoForm.ativo,
      };

      if (permissaoForm.module_scope_mode === 'custom') {
        const moduleKeys = modulosDisponiveisParaPermissao
          .filter((moduleItem) => moduleItem.company_enabled)
          .map((moduleItem) => moduleItem.key)
          .filter((key) => permissaoForm.module_keys.includes(key));

        if (moduleKeys.length === 0) {
          toast.error('Selecione ao menos um modulo para o escopo personalizado');
          return;
        }

        payload.module_keys = moduleKeys;
      } else if (permissaoForm.id) {
        payload.clear_module_scope = true;
      }

      if (permissaoForm.id) {
        await axios.put(`/api/admin/permissoes/${permissaoForm.id}`, payload);
        toast.success('Permissao atualizada');
      } else {
        await axios.post('/api/admin/permissoes', payload);
        toast.success('Permissao criada');
      }

      setShowPermissaoModal(false);
      await carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar permissao');
    }
  };

  const excluirPermissao = async (id) => {
    if (!window.confirm('Deseja remover esta permissao?')) return;
    try {
      await axios.delete(`/api/admin/permissoes/${id}`);
      toast.success('Permissao removida');
      await carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao remover permissao');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestao Principal</h1>
          <p className="text-gray-600">Empresas, usuarios e permissoes de acesso do tenant</p>
        </div>
        {loading && <div className="loader h-6 w-6" />}
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Empresas</p>
            <p className="text-2xl font-bold text-gray-900">{overview.empresas}</p>
            <p className="mt-1 text-xs text-gray-500">Ativas: {empresasAtivas}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Usuarios</p>
            <p className="text-2xl font-bold text-gray-900">{overview.usuarios}</p>
            <p className="mt-1 text-xs text-gray-500">Ativos: {usuariosAtivos}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Permissoes</p>
            <p className="text-2xl font-bold text-gray-900">{overview.permissoes}</p>
            <p className="mt-1 text-xs text-gray-500">Vinculos por empresa</p>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header flex items-center justify-between">
          <span>Empresas ({empresas.length})</span>
          <button className="btn-primary" onClick={abrirNovaEmpresa}>
            <PlusIcon className="mr-2 h-5 w-5" />
            Nova Empresa
          </button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CNPJ</th>
                  <th>Email</th>
                  <th>Modulos liberados</th>
                  <th>Status</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {empresas.map((empresa) => {
                  const enabledModules = Array.isArray(empresa.modules)
                    ? empresa.modules.filter((moduleItem) => moduleItem.enabled)
                    : [];

                  return (
                    <tr key={empresa.id}>
                      <td className="font-medium">{empresa.nome}</td>
                      <td>{empresa.cnpj || '-'}</td>
                      <td>{empresa.email || '-'}</td>
                      <td>{renderModuleBadges(enabledModules)}</td>
                      <td>
                        <span className={empresa.ativa ? 'badge-success' : 'badge-danger'}>
                          {empresa.ativa ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td>
                        <button className="p-1 text-primary-600 hover:text-primary-900" onClick={() => abrirEditarEmpresa(empresa)}>
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {empresas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-500">
                      Nenhuma empresa cadastrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <div className="card mb-6">
        <div className="card-header flex items-center justify-between">
          <span>Usuarios ({usuarios.length})</span>
          <button className="btn-primary" onClick={abrirNovoUsuario}>
            <PlusIcon className="mr-2 h-5 w-5" />
            Novo Usuario
          </button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Perfil Global</th>
                  <th>Status</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((usuario) => (
                  <tr key={usuario.id}>
                    <td className="font-medium">{usuario.nome}</td>
                    <td>{usuario.email}</td>
                    <td className="capitalize">{usuario.role_global}</td>
                    <td>
                      <span className={usuario.ativo ? 'badge-success' : 'badge-danger'}>
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <button className="p-1 text-primary-600 hover:text-primary-900" onClick={() => abrirEditarUsuario(usuario)}>
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {usuarios.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-gray-500">
                      Nenhum usuario cadastrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <span>Permissoes ({permissoesFiltradas.length} de {permissoes.length})</span>
          <button className="btn-primary" onClick={abrirNovaPermissao}>
            <PlusIcon className="mr-2 h-5 w-5" />
            Nova Permissao
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className="form-label">
                <MagnifyingGlassIcon className="mr-1 inline h-4 w-4" />
                Buscar acesso
              </label>
              <input
                className="form-input"
                value={permissaoSearch}
                onChange={(e) => setPermissaoSearch(e.target.value)}
                placeholder="Usuario, email, empresa ou perfil"
              />
            </div>
            <div>
              <label className="form-label">Empresa</label>
              <select className="form-select" value={permissaoEmpresaFiltro} onChange={(e) => setPermissaoEmpresaFiltro(e.target.value)}>
                <option value="todas">Todas</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">
                <FunnelIcon className="mr-1 inline h-4 w-4" />
                Status
              </label>
              <select className="form-select" value={permissaoStatusFiltro} onChange={(e) => setPermissaoStatusFiltro(e.target.value)}>
                <option value="ativas">Ativas</option>
                <option value="inativas">Inativas</option>
                <option value="todas">Todas</option>
              </select>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Empresa</th>
                  <th>Perfil</th>
                  <th>Escopo de modulos</th>
                  <th>Status</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {permissoesFiltradas.map((item) => {
                  const scopedModules = Array.isArray(item.module_keys)
                    ? item.module_keys.map((moduleKey) => moduleCatalogMap[moduleKey]).filter(Boolean)
                    : [];

                  return (
                    <tr key={item.id}>
                      <td>
                        <p className="font-medium">{item.usuario_nome}</p>
                        <p className="text-xs text-gray-500">{item.usuario_email}</p>
                      </td>
                      <td>{item.empresa_nome}</td>
                      <td className="capitalize">{item.perfil}</td>
                      <td>
                        {item.explicit_module_scope ? (
                          renderModuleBadges(scopedModules, 'Sem modulos ativos')
                        ) : (
                          <span className="text-sm text-gray-600">Todos os modulos liberados da empresa</span>
                        )}
                      </td>
                      <td>
                        <span className={item.ativo ? 'badge-success' : 'badge-danger'}>
                          {item.ativo ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button className="p-1 text-primary-600 hover:text-primary-900" onClick={() => abrirEditarPermissao(item)}>
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button className="p-1 text-danger-600 hover:text-danger-900" onClick={() => excluirPermissao(item.id)}>
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {permissoesFiltradas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-500">
                      Nenhuma permissao encontrada para os filtros atuais
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showEmpresaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-semibold">{empresaForm.id ? 'Editar Empresa' : 'Nova Empresa'}</h3>
              <button onClick={() => setShowEmpresaModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="form-label">Nome</label>
                <input className="form-input" value={empresaForm.nome} onChange={(e) => setEmpresaForm({ ...empresaForm, nome: e.target.value })} />
              </div>
              <div>
                <label className="form-label">CNPJ</label>
                <input className="form-input" value={empresaForm.cnpj} onChange={(e) => setEmpresaForm({ ...empresaForm, cnpj: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Telefone</label>
                <input className="form-input" value={empresaForm.telefone} onChange={(e) => setEmpresaForm({ ...empresaForm, telefone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Email</label>
                <input className="form-input" value={empresaForm.email} onChange={(e) => setEmpresaForm({ ...empresaForm, email: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Endereco</label>
                <textarea
                  className="form-textarea"
                  rows="3"
                  value={empresaForm.endereco}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, endereco: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={empresaForm.ativa} onChange={(e) => setEmpresaForm({ ...empresaForm, ativa: e.target.checked })} />
                  Empresa ativa
                </label>
              </div>
              {!empresaForm.id && (
                <div className="md:col-span-2 rounded-lg border border-primary-100 bg-primary-50 p-4">
                  <label className="inline-flex items-start gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={empresaForm.criar_plano_contas_padrao !== false}
                      onChange={(e) => setEmpresaForm({ ...empresaForm, criar_plano_contas_padrao: e.target.checked })}
                    />
                    <span>
                      <span className="block font-medium text-gray-900">Criar plano de contas padrao</span>
                      <span className="mt-1 block text-xs text-gray-600">
                        Inclui receitas, deducoes, custos, despesas operacionais, financeiras, tributarias e patrimonio para a empresa iniciar pronta.
                      </span>
                    </span>
                  </label>
                </div>
              )}
              <div className="md:col-span-2">
                <div className="mb-3">
                  <label className="form-label">Modulos contratados</label>
                  <p className="text-xs text-gray-500">Cada empresa pode operar qualquer combinacao de modulos de forma independente.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {moduleCatalog.map((moduleItem) => (
                    <label key={moduleItem.key} className="rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={empresaForm.modules[moduleItem.key] === true}
                          onChange={(e) =>
                            setEmpresaForm({
                              ...empresaForm,
                              modules: {
                                ...empresaForm.modules,
                                [moduleItem.key]: e.target.checked,
                              },
                            })
                          }
                        />
                        <div>
                          <p className="font-medium text-gray-900">{moduleItem.nome}</p>
                          <p className="mt-1 text-sm text-gray-500">{moduleItem.descricao || 'Modulo sem descricao cadastrada.'}</p>
                          {Array.isArray(moduleItem.capabilities) && moduleItem.capabilities.length > 0 && (
                            <p className="mt-2 text-xs text-gray-500">Capacidades: {moduleItem.capabilities.join(', ')}</p>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                  {moduleCatalog.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                      Nenhum modulo encontrado no catalogo.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 border-t px-6 py-4">
              <button className="btn-secondary" onClick={() => setShowEmpresaModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvarEmpresa}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {showUsuarioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-semibold">{usuarioForm.id ? 'Editar Usuario' : 'Novo Usuario'}</h3>
              <button onClick={() => setShowUsuarioModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="form-label">Nome</label>
                <input className="form-input" value={usuarioForm.nome} onChange={(e) => setUsuarioForm({ ...usuarioForm, nome: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Email</label>
                <input className="form-input" value={usuarioForm.email} onChange={(e) => setUsuarioForm({ ...usuarioForm, email: e.target.value })} />
              </div>
              <div>
                <label className="form-label">{usuarioForm.id ? 'Nova Senha (opcional)' : 'Senha'}</label>
                <input
                  type="password"
                  className="form-input"
                  value={usuarioForm.senha}
                  onChange={(e) => setUsuarioForm({ ...usuarioForm, senha: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Perfil Global</label>
                <select
                  className="form-select"
                  value={usuarioForm.role_global}
                  onChange={(e) => setUsuarioForm({ ...usuarioForm, role_global: e.target.value })}
                >
                  {ROLE_GLOBAL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={usuarioForm.ativo} onChange={(e) => setUsuarioForm({ ...usuarioForm, ativo: e.target.checked })} />
                  Usuario ativo
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 border-t px-6 py-4">
              <button className="btn-secondary" onClick={() => setShowUsuarioModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvarUsuario}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {showPermissaoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-semibold">{permissaoForm.id ? 'Editar Permissao' : 'Nova Permissao'}</h3>
              <button onClick={() => setShowPermissaoModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="form-label">Tipo de acesso</label>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {PERMISSAO_PRESETS.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        activePresetKey === preset.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-primary-300'
                      }`}
                      onClick={() => aplicarPresetPermissao(preset)}
                    >
                      <div className="flex items-start gap-2">
                        <CheckCircleIcon className={`mt-0.5 h-5 w-5 ${activePresetKey === preset.key ? 'text-primary-600' : 'text-gray-300'}`} />
                        <div>
                          <p className="font-medium text-gray-900">{preset.label}</p>
                          <p className="mt-1 text-xs text-gray-500">{preset.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="form-label">Usuario</label>
                <input
                  className="form-input mb-2"
                  value={usuarioModalSearch}
                  onChange={(e) => setUsuarioModalSearch(e.target.value)}
                  placeholder="Buscar por nome ou email"
                  disabled={Boolean(permissaoForm.id)}
                />
                <select
                  className="form-select"
                  value={permissaoForm.usuario_id}
                  onChange={(e) => setPermissaoForm({ ...permissaoForm, usuario_id: e.target.value })}
                  disabled={Boolean(permissaoForm.id)}
                >
                  <option value="">Selecione</option>
                  {usuariosFiltradosModal.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nome} ({usuario.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Empresa</label>
                <input
                  className="form-input mb-2"
                  value={empresaModalSearch}
                  onChange={(e) => setEmpresaModalSearch(e.target.value)}
                  placeholder="Buscar por nome, CNPJ ou email"
                  disabled={Boolean(permissaoForm.id)}
                />
                <select
                  className="form-select"
                  value={permissaoForm.empresa_id}
                  onChange={(e) => {
                    const nextEmpresaId = e.target.value;
                    const nextEmpresa = empresas.find((empresa) => empresa.id === nextEmpresaId) || null;
                    const nextCompanyModuleMap = getCompanyModuleMap(nextEmpresa);
                    setPermissaoForm({
                      ...permissaoForm,
                      empresa_id: nextEmpresaId,
                      module_keys: permissaoForm.module_keys.filter((key) => nextCompanyModuleMap[key] === true),
                    });
                  }}
                  disabled={Boolean(permissaoForm.id)}
                >
                  <option value="">Selecione</option>
                  {empresasFiltradasModal.map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>
                      {empresa.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Perfil na Empresa</label>
                <select className="form-select" value={permissaoForm.perfil} onChange={(e) => setPermissaoForm({ ...permissaoForm, perfil: e.target.value })}>
                  {PERFIL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <label className="mt-6 inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={permissaoForm.ativo} onChange={(e) => setPermissaoForm({ ...permissaoForm, ativo: e.target.checked })} />
                  Vinculo ativo
                </label>
              </div>
              <div className="md:col-span-2 rounded-lg border border-gray-200 p-4">
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-900">Escopo de modulos</p>
                  <p className="mt-1 text-sm text-gray-500">
                    Use escopo da empresa para liberar tudo que a empresa comprou, ou personalize por usuario.
                  </p>
                </div>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3">
                    <input
                      type="radio"
                      name="module_scope_mode"
                      checked={permissaoForm.module_scope_mode === 'company'}
                      onChange={() => setPermissaoForm({ ...permissaoForm, module_scope_mode: 'company' })}
                    />
                    <div>
                      <p className="font-medium text-gray-900">Usar todos os modulos liberados da empresa</p>
                      <p className="text-sm text-gray-500">Nao cria restricao adicional para o usuario.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3">
                    <input
                      type="radio"
                      name="module_scope_mode"
                      checked={permissaoForm.module_scope_mode === 'custom'}
                      onChange={() => setPermissaoForm({ ...permissaoForm, module_scope_mode: 'custom' })}
                    />
                    <div>
                      <p className="font-medium text-gray-900">Selecionar modulos especificos</p>
                      <p className="text-sm text-gray-500">Limita o usuario a uma parcela dos modulos da empresa.</p>
                    </div>
                  </label>
                </div>

                {permissaoForm.module_scope_mode === 'custom' && (
                  <div className="mt-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-gray-500">
                        {permissaoForm.module_keys.length} modulo(s) selecionado(s)
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          onClick={() =>
                            setPermissaoForm({
                              ...permissaoForm,
                              module_keys: modulosDisponiveisParaPermissao
                                .filter((moduleItem) => moduleItem.company_enabled)
                                .map((moduleItem) => moduleItem.key),
                            })
                          }
                        >
                          Marcar liberados
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          onClick={() => setPermissaoForm({ ...permissaoForm, module_keys: [] })}
                        >
                          Limpar
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {modulosDisponiveisParaPermissao.map((moduleItem) => (
                        <label
                          key={moduleItem.key}
                          className={`rounded-lg border p-4 ${moduleItem.company_enabled ? 'border-gray-200' : 'border-dashed border-gray-300 bg-gray-50'}`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={permissaoForm.module_keys.includes(moduleItem.key)}
                              disabled={!moduleItem.company_enabled}
                              onChange={(e) => {
                                const currentKeys = new Set(permissaoForm.module_keys);
                                if (e.target.checked) {
                                  currentKeys.add(moduleItem.key);
                                } else {
                                  currentKeys.delete(moduleItem.key);
                                }

                                setPermissaoForm({
                                  ...permissaoForm,
                                  module_keys: Array.from(currentKeys),
                                });
                              }}
                            />
                            <div>
                              <p className="font-medium text-gray-900">{moduleItem.nome}</p>
                              <p className="mt-1 text-sm text-gray-500">{moduleItem.descricao || 'Modulo sem descricao cadastrada.'}</p>
                              {!moduleItem.company_enabled && (
                                <p className="mt-2 text-xs text-amber-700">Este modulo nao esta liberado para a empresa selecionada.</p>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                      {modulosDisponiveisParaPermissao.length === 0 && (
                        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                          Nenhum modulo disponivel para selecao.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 border-t px-6 py-4">
              <button className="btn-secondary" onClick={() => setShowPermissaoModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvarPermissao}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoSaaS;
